<template>
  <div class="hollow-dots-spinner" :style="spinnerStyle">
    <div
      v-for="(ds, index) in dotsStyles"
      :key="index"
      class="dot"
      :style="ds"
    ></div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'

// Source: epic-spinners library,
// https://github.com/epicmaxco/epic-spinners/blob/master/src/components/lib/HollowDotsSpinner.vue

const props = withDefaults(
  defineProps<{
    animationDuration?: number
    dotSize?: number
    dotsNum?: number
    color?: string
  }>(),
  {
    animationDuration: 1000,
    dotSize: 15,
    dotsNum: 3,
    color: '#fff',
  },
)

const horizontalMargin = computed(() => {
  return props.dotSize / 2
})

const spinnerStyle = computed(() => {
  return {
    height: `${props.dotSize}px`,
    width: `${(props.dotSize + horizontalMargin.value * 2) * props.dotsNum}px`,
  }
})

const dotStyle = computed(() => {
  return {
    animationDuration: `${props.animationDuration}ms`,
    width: `${props.dotSize}px`,
    height: `${props.dotSize}px`,
    margin: `0 ${horizontalMargin.value}px`,
    borderWidth: `${props.dotSize / 5}px`,
    borderColor: props.color,
  }
})

const dotsStyles = computed(() => {
  const dotsStyles = []
  const delayModifier = 0.3
  const basicDelay = 1000
  for (let i = 1; i <= props.dotsNum; i++) {
    const style = Object.assign(
      {
        animationDelay: `${basicDelay * i * delayModifier}ms`,
      },
      dotStyle.value,
    )
    dotsStyles.push(style)
  }
  return dotsStyles
})
</script>

<style scoped>
.hollow-dots-spinner,
.hollow-dots-spinner * {
  box-sizing: border-box;
}
.hollow-dots-spinner {
  height: 15px;
  width: calc(30px * 3);
}
.hollow-dots-spinner .dot {
  width: 15px;
  height: 15px;
  margin: 0 calc(15px / 2);
  border: calc(15px / 5) solid #ff1d5e;
  border-radius: 50%;
  float: left;
  transform: scale(0);
  animation: hollow-dots-spinner-animation 1000ms ease infinite 0ms;
}
.hollow-dots-spinner .dot:nth-child(1) {
  animation-delay: calc(300ms * 1);
}
.hollow-dots-spinner .dot:nth-child(2) {
  animation-delay: calc(300ms * 2);
}
.hollow-dots-spinner .dot:nth-child(3) {
  animation-delay: calc(300ms * 3);
}
@keyframes hollow-dots-spinner-animation {
  50% {
    transform: scale(1);
    opacity: 1;
  }
  100% {
    opacity: 0;
  }
}
</style>
